/**
 * Per-conversation queue of OUTLINE edits the user has made in the storyboard/outline
 * (archetype switch, slide reorder, description edits) but not yet sent to the Ivy agent.
 *
 * The Ivy composer renders these as removable/expandable pills above the input. On the normal
 * composer Send, the queued edits are appended to the typed message as one combined instruction
 * and the queue is cleared — the agent then re-plans / updates the outline accordingly.
 */
import { create } from 'zustand';
import { formatStoryboardInputLine, type StoryboardInputBody } from '@agentic-ivy/shared-types';
import { pageLabel } from '@/components/video-workspace/storyboard/feedbackWords';

export type OutlineEditKind = 'archetype' | 'reorder' | 'edit' | 'comment' | 'storyboard-input';

export interface OutlineEditItem {
  id: string;
  kind: OutlineEditKind;
  /** Scene this edit targets (omitted for whole-outline edits like reorder). */
  slideId?: string;
  /** For comments: the item the comment is anchored to (archetype / narration / act-2 / ost-1 …). */
  anchorId?: string;
  /** Human label for the anchored item, shown inline on the storyboard ("Page 2", "Narration"). */
  anchorLabel?: string;
  /** Short label shown on the collapsed pill. */
  summary: string;
  /** Full natural-language instruction sent to the agent (editable when expanded). */
  message: string;
  /** Collapses repeated edits of the same target: a new edit with the same key replaces the old. */
  dedupeKey: string;
  /** For storyboard inputs: the stored record's id (in_<n>). */
  inputId?: string;
  /** For storyboard inputs: the change in the page's words, with its picture (the quiet line while Ivy is busy). */
  change?: { words: string; image?: string };
}

export interface StoryboardDisplay {
  summaries?: Record<string, string>;
  images?: Record<string, string>;
}

interface State {
  queuesByConversation: Record<string, OutlineEditItem[]>;
  /** Optimistic per-slide archetype override after a switch, until the agent re-plans the outline.
   *  Survives queue clear (the agent applies it later); cleared when the outline catches up. */
  pendingArchetypes: Record<string, Record<string, string>>;
  /** The page's words and pictures for each storyboard send this session, kept after it leaves the queue (the thread's card reads them). */
  displayByInput: Record<string, StoryboardDisplay>;

  setPendingArchetype(conversationId: string, slideId: string, archetypeKey: string): void;
  clearPendingArchetype(conversationId: string, slideId: string): void;

  enqueue(conversationId: string, item: Omit<OutlineEditItem, 'id'>): void;
  addComment(conversationId: string, c: { slideId: string; sceneLabel: string; anchorId: string; anchorLabel: string; text: string }): void;
  enqueueStoryboardInput(conversationId: string, inputId: string, body: StoryboardInputBody): void;
  updateMessage(conversationId: string, id: string, message: string): void;
  remove(conversationId: string, id: string): void;
  clear(conversationId: string): void;
  items(conversationId: string): OutlineEditItem[];
  /** Comments for a slide, grouped by the anchored item id (for inline rendering on the storyboard). */
  commentsByAnchor(conversationId: string, slideId: string): Record<string, OutlineEditItem[]>;
}

let seq = 0;
const nextId = () => `oe-${++seq}`;

export const useOutlineEditQueueStore = create<State>((set, get) => ({
  queuesByConversation: {},
  pendingArchetypes: {},
  displayByInput: {},

  setPendingArchetype(conversationId, slideId, archetypeKey) {
    set((state) => ({
      pendingArchetypes: {
        ...state.pendingArchetypes,
        [conversationId]: { ...(state.pendingArchetypes[conversationId] ?? {}), [slideId]: archetypeKey },
      },
    }));
  },

  clearPendingArchetype(conversationId, slideId) {
    set((state) => {
      const conv = state.pendingArchetypes[conversationId];
      if (!conv || !(slideId in conv)) return state;
      const { [slideId]: _removed, ...rest } = conv;
      return { pendingArchetypes: { ...state.pendingArchetypes, [conversationId]: rest } };
    });
  },

  enqueue(conversationId, item) {
    set((state) => {
      const current = state.queuesByConversation[conversationId] ?? [];
      // Replace any existing edit with the same dedupeKey (latest wins), else append.
      const withoutDupe = current.filter((q) => q.dedupeKey !== item.dedupeKey);
      return {
        queuesByConversation: {
          ...state.queuesByConversation,
          [conversationId]: [...withoutDupe, { ...item, id: nextId() }],
        },
      };
    });
  },

  addComment(conversationId, c) {
    // Comments never collapse — unique dedupeKey per comment.
    get().enqueue(conversationId, {
      kind: 'comment',
      slideId: c.slideId,
      anchorId: c.anchorId,
      anchorLabel: c.anchorLabel,
      summary: `Comment · ${c.sceneLabel} · ${c.anchorLabel}`,
      message: `Feedback on ${c.sceneLabel} → ${c.anchorLabel}: ${c.text}`,
      dedupeKey: `comment:${c.slideId}:${c.anchorId}:${nextId()}`,
    });
  },

  enqueueStoryboardInput(conversationId, inputId, body) {
    const fieldCount = body.touched.length || Object.keys(body.fields).length;
    if (body.summaries || body.images) {
      set((state) => ({ displayByInput: { ...state.displayByInput, [inputId]: { summaries: body.summaries, images: body.images } } }));
    }
    get().enqueue(conversationId, {
      kind: 'storyboard-input',
      inputId,
      summary: `Input ${inputId} · v${body.storyboardVersion} · ${fieldCount} field${fieldCount === 1 ? '' : 's'}${body.lock ? ' · lock' : ''}`,
      message: formatStoryboardInputLine({ storyboardVersion: body.storyboardVersion, fields: body.fields, touched: body.touched, id: inputId, lock: !!body.lock }),
      dedupeKey: `storyboard-input:${inputId}`,
      change: changeOf(body),
    });
  },

  updateMessage(conversationId, id, message) {
    set((state) => {
      const current = state.queuesByConversation[conversationId];
      if (!current) return state;
      return {
        queuesByConversation: {
          ...state.queuesByConversation,
          [conversationId]: current.map((q) => (q.id === id ? { ...q, message } : q)),
        },
      };
    });
  },

  remove(conversationId, id) {
    set((state) => {
      const current = state.queuesByConversation[conversationId];
      if (!current) return state;
      return {
        queuesByConversation: {
          ...state.queuesByConversation,
          [conversationId]: current.filter((q) => q.id !== id),
        },
      };
    });
  },

  clear(conversationId) {
    set((state) => ({
      queuesByConversation: { ...state.queuesByConversation, [conversationId]: [] },
    }));
  },

  items(conversationId) {
    return get().queuesByConversation[conversationId] ?? [];
  },

  commentsByAnchor(conversationId, slideId) {
    const out: Record<string, OutlineEditItem[]> = {};
    for (const it of get().queuesByConversation[conversationId] ?? []) {
      if (it.kind === 'comment' && it.slideId === slideId && it.anchorId) {
        (out[it.anchorId] ??= []).push(it);
      }
    }
    return out;
  },
}));

/** The queue line's words for one change the page sent in words: "Night paper look", "Remove the watermark turned on". */
function oneChange(name: string, said: string): string {
  if (said.startsWith('“')) return 'Your note';
  const m = said.match(/^([^:]{1,40}):\s+(.{1,40})$/);
  if (!m) return 'Your storyboard change';
  const label = pageLabel(name, m[1]);
  if (/^(feedback|notes?)$/i.test(label) && m[2] === 'cleared') return 'Your note cleared';
  if (/^(on|off)$/.test(m[2])) return `${label} turned ${m[2]}`;
  return `${m[2]} ${label.toLowerCase()}`;
}

/** A storyboard send in a few words for the quiet queue line: "Night paper look", from the page's own "Look: Night paper". */
function changeOf(body: StoryboardInputBody): { words: string; image?: string } {
  const said = body.touched.map((n) => body.summaries?.[n]).filter((s): s is string => !!s);
  const image = body.touched.map((n) => body.images?.[n]).find((s): s is string => !!s && /^https:\/\//.test(s));
  const words = body.lock ? 'Your approval'
    : said.length === 1 && body.touched.length === 1 ? oneChange(body.touched[0], said[0])
      : said.length > 1 ? `${said.length} storyboard changes`
        : 'Your storyboard change';
  return image ? { words, image } : { words };
}

/** The hidden context a send of queued items carries: the storyboard's for storyboard sends alone, else the outline's. */
export function queuedContext(items: OutlineEditItem[]): string {
  return items.every((it) => it.kind === 'storyboard-input') ? '[view: storyboard] [action: storyboard_input]' : '[view: outline] [action: outline_edits]';
}

/** Combine queued outline edits into one instruction block appended to the user's message. */
export function buildOutlineEditBlock(items: OutlineEditItem[]): string {
  const edits = items.filter((it) => it.kind !== 'storyboard-input');
  if (edits.length === 0) return '';
  const lines = edits.map((it, i) => `${i + 1}. ${it.message}`);
  return `Outline changes to apply (re-plan / update the outline accordingly):\n${lines.join('\n')}`;
}

/**
 * The chat message a send produces: storyboard-input lines first (the agent's rule keys on a message
 * that BEGINS with one), then the typed text, then the outline-edit block.
 */
export function composeQueuedMessage(text: string, items: OutlineEditItem[]): string {
  const inputLines = items.filter((it) => it.kind === 'storyboard-input').map((it) => it.message);
  return [...inputLines, text.trim(), buildOutlineEditBlock(items)].filter(Boolean).join('\n\n');
}
